import { get as getCookie } from 'browser-cookies'
import { zonedTimeToUtc } from 'date-fns-tz'
import IdleJs from 'idle-js'

export function scrambleAlphanumeric(input: string) {
  // Scrambling is done as per-business requirements.
  return input && input.replace(/[a-z]/gi, 'a').replace(/\d/gi, '0')
}

export function hasOwnNestedProperty(
  sourceObject: object,
  propertyPath: string,
): boolean {
  if (!sourceObject || !propertyPath) {
    return false
  }

  let obj: any = sourceObject
  const properties = propertyPath.split('.')

  for (let i = 0, len = properties.length; i < len; i++) {
    const prop: any = properties[i]

    if (!obj || !Object.prototype.hasOwnProperty.call(obj, prop)) {
      return false
    }

    obj = obj[prop]
  }

  return true
}

export function deepSetProperty(
  source: object,
  prop: string | Array<string>,
  value: any,
) {
  const obj: any = source

  if (typeof prop === 'string') {
    prop = prop.split('.')
  }

  if (prop.length > 1) {
    const e: any = prop.shift()

    deepSetProperty(
      (obj[e] =
        Object.prototype.toString.call(obj[e]) === '[object Object]'
          ? obj[e]
          : {}),
      prop,
      value,
    )
  } else {
    obj[prop[0]] = value
  }
}

export function hashCode(str: string): number {
  let hash = 0
  let i
  let chr

  if (str.length === 0) {
    return hash
  }

  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i)
    hash = (hash << 5) - hash + chr // eslint-disable-line no-bitwise
    // Convert to 32bit integer
    hash |= 0 // eslint-disable-line no-bitwise
  }

  return hash
}

export function snakeToCamelCase(source: string): string {
  return source.replace(/_([a-z])/g, m => m.toUpperCase()).replace(/_/g, '')
}

export function kebabToCamelCase(source: string): string {
  return source.replace(/-([a-z])/g, m => m.toUpperCase()).replace(/-/g, '')
}

export function firstCharToLowerCase(source: string): string {
  return source[0].toLowerCase() + source.slice(1)
}

export function toCamelCase(source: string): string {
  return firstCharToLowerCase(snakeToCamelCase(kebabToCamelCase(source)))
}

export function cameliseObjectProperties(source: object): any {
  const resultingObject = {}
  const entries = Object.entries(source)

  for (let i = 0; i < entries.length; i++) {
    const key = entries[i][0]
    let value = entries[i][1]

    const camelKey = toCamelCase(key)

    if (typeof value === 'object') {
      value = cameliseObjectProperties(value)
    }

    if (
      key !== camelKey &&
      Object.prototype.hasOwnProperty.call(source, camelKey)
    ) {
      throw new Error(
        `Multiples of ${key} that can become camelCase key candidates`,
      )
    }

    resultingObject[camelKey] = value
  }

  return resultingObject
}

export function generateUUID(): string {
  let currentTime = new Date().getTime()

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const r = (currentTime + Math.random() * 16) % 16 | 0 // eslint-disable-line no-bitwise
    currentTime = Math.floor(currentTime / 16) // eslint-disable-line no-bitwise
    return (char === 'x' ? r : (r & 0x3) | 0x8).toString(16) // eslint-disable-line no-bitwise
  })
}

export function getFromLocalStorageOrCookie(
  keyName: string,
): string | undefined {
  const valueFromLocalStorage = localStorage.getItem(keyName)

  if (valueFromLocalStorage) {
    return valueFromLocalStorage
  }

  const valuesFromCookies = getCookie(keyName)

  if (valuesFromCookies) {
    return valuesFromCookies
  }

  return undefined
}

export function onIdle(func: Function, idleSeconds = 60) {
  return new IdleJs({
    idle: idleSeconds * 1000,
    events: ['mousemove', 'keydown', 'mousedown', 'touchstart'],
    onIdle: function runOnIdle() {
      func()
    },
  }).start()
}

export function promoteArrayElement(
  sourceArray: Array<any>,
  key: string,
  expectedValue: any,
) {
  for (let i = 0, len = sourceArray.length; i < len; i++) {
    if (sourceArray[i][key] === expectedValue) {
      const a = sourceArray.splice(i, 1)
      sourceArray.unshift(a[0])
      break
    }
  }

  return sourceArray
}

export function parseDate(stringDate: string) {
  const date = new Date(stringDate)

  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()

  return `${day}.${month}.${year}`
}

export function isUuid(string: string) {
  const regexUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return regexUuid.test(string)
}

/**
 * We use the split.reverse.join “hack” many places in our app, but this function returns the same
 * German-ready date string.
 */
export function toGermanDateString(date: string | Date): string {
  const dateObject = new Date(date)

  const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }

  return dateObject.toLocaleDateString('de-DE', options)
}

export function makeDateTimeTz(
  date: string,
  time?: string,
  timeZone: string = 'Europe/Berlin',
): Date {
  return zonedTimeToUtc(`${date} ${time}`, timeZone)
}

export function getTimeFromDateOfFirstAppointment(
  stringDate: string,
  timeZone = 'Europe/Berlin',
) {
  const date = new Date(stringDate)

  return new Intl.DateTimeFormat('de-DE', {
    timeZone,
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).format(date)
}

export function isProduction() {
  return /www\.careship/.test(window.location.hostname)
}

export function getCurrentEnvironment() {
  return isProduction() ? 'production' : 'staging'
}

export function getGenderString(gender: string): string {
  const m = new Map([
    ['M', 'male'],
    ['male', 'male'],
    ['F', 'female'],
    ['female', 'female'],
  ])

  if (m.has(gender)) {
    return m.get(gender)
  }

  return null
}
